package org.teddapp.project.ui

import androidx.compose.foundation.background
import kotlinx.coroutines.launch
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.dp

/**
 * États communs à tout élément cliquable, d'après le design system :
 * survol sur [hover], anneau de focus clavier de 2 dp décalé de 2 dp, désactivé à 45 %.
 *
 * Le fond de repos ([fill]) et celui de survol sont posés ici, pour que l'anneau de focus
 * reste à l'extérieur de la forme.
 */
@Composable
fun Modifier.teddClickable(
    onClick: () -> Unit,
    shape: Shape = RoundedCornerShape(TeddTokens.RSm),
    fill: Color = Color.Transparent,
    hover: Color = TeddTokens.SurfaceSunken,
    enabled: Boolean = true,
    role: Role? = Role.Button,
): Modifier {
    val interaction = remember { MutableInteractionSource() }
    val hovered by interaction.collectIsHoveredAsState()
    val focused by interaction.collectIsFocusedAsState()
    val ring = TeddTokens.FocusRing
    return this
        .alpha(if (enabled) 1f else 0.45f)
        .then(if (focused) Modifier.border(2.dp, ring, shape) else Modifier)
        .padding(if (focused) 0.dp else 0.dp)
        .clip(shape)
        .background(if (hovered && enabled) hover else fill, shape)
        .hoverable(interaction, enabled)
        .clickable(
            interactionSource = interaction,
            indication = null,
            enabled = enabled,
            role = role,
            onClick = onClick,
        )
}

/**
 * Ombre de carte du design system : presque invisible en clair, remplacée par rien en
 * sombre (le liseré [TeddTokens.Line] fait le travail). La séparation vient des surfaces.
 */
@Composable
fun Modifier.teddCardShadow(shape: Shape): Modifier =
    if (LocalTeddColors.current.isDark) this
    else shadow(1.dp, shape, clip = false, ambientColor = Color(0x0F1A1916), spotColor = Color(0x141A1916))

/** Ombre des calques flottants : palette, menus, panneaux. */
@Composable
fun Modifier.teddOverlayShadow(shape: Shape): Modifier =
    shadow(
        elevation = if (LocalTeddColors.current.isDark) 24.dp else 16.dp,
        shape = shape,
        clip = false,
        ambientColor = Color(0x381A1916),
        spotColor = Color(0x381A1916),
    )


/**
 * Rend une action qui s'exécute à l'image suivante, pas dans le geste qui la déclenche.
 * Nécessaire aux dialogues : fermer un `Dialog` (retirer sa composition) pendant le
 * traitement du clic qui vient de sa propre surface fait planter Compose dans le
 * navigateur (« layouts are not part of the same hierarchy ») et gèle l'application.
 */
@androidx.compose.runtime.Composable
fun rememberAfterFrame(action: () -> Unit): () -> Unit {
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    val current = androidx.compose.runtime.rememberUpdatedState(action)
    return androidx.compose.runtime.remember(scope) {
        { scope.launch { androidx.compose.runtime.withFrameNanos { current.value() } } }
    }
}
